import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { fmtDate, fmtDateTime, inr } from "@/lib/format";
import { StatusChip, riderStatusTone, invoiceStatusTone } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

function SummaryCard({ label, value, testid, accent = "" }) {
  return (
    <div className="card-tactical p-4" data-testid={testid}>
      <div className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`mt-1.5 text-2xl font-extrabold font-heading font-mono ${accent}`}>{value}</div>
    </div>
  );
}

export default function RiderLedger() {
  const { riderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [depAmount, setDepAmount] = useState("");
  const [depStatus, setDepStatus] = useState("Pending");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/riders/${riderId}/ledger`).then((r) => {
    setData(r.data);
    setDepAmount(String(r.data.rider.deposit_amount ?? ""));
    setDepStatus(r.data.rider.deposit_status || "Pending");
  }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [riderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveDeposit = async () => {
    setBusy(true);
    try {
      await api.patch(`/riders/${riderId}/deposit`,
        { deposit_amount: parseFloat(depAmount) || 0, deposit_status: depStatus });
      toast.success("Deposit updated");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!data) return <div className="text-slate-400">Loading ledger…</div>;
  const { rider, invoices, swaps, summary } = data;

  return (
    <div className="space-y-5" data-testid="rider-ledger-page">
      <Button variant="ghost" size="sm" data-testid="ledger-back-btn" onClick={() => navigate("/riders")}
        className="text-slate-400 -ml-2">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Riders
      </Button>

      <div className="card-tactical p-5" data-testid="ledger-rider-card">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">{rider.name}</h1>
          <span className="font-mono text-sm text-slate-500">{rider.rider_id}</span>
          <StatusChip label={rider.status} tone={riderStatusTone(rider.status)} testid="ledger-rider-status" />
          <StatusChip label={rider.payment.chip} tone={rider.payment.tone} testid="ledger-payment-chip" />
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm text-slate-400">
          <div>Mobile<div className="font-mono text-slate-700">{rider.mobile}</div></div>
          <div>Area<div className="text-slate-700">{rider.area || "—"}</div></div>
          <div>Payment Day<div className="text-slate-700">{rider.payment_day}</div></div>
          <div>Vehicle<div className="font-mono text-slate-700">{rider.vehicle_id || "—"}</div></div>
          <div>Battery<div className="font-mono text-slate-700">{rider.battery_id || "—"}</div></div>
          <div>Onboarded<div className="text-slate-700">{fmtDate(rider.date_onboarded)}</div></div>
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-border pt-4" data-testid="deposit-panel">
          <div className="space-y-1">
            <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Security Deposit (₹)</label>
            <Input data-testid="deposit-amount-input" type="number" min="0" value={depAmount}
              onChange={(e) => setDepAmount(e.target.value)} disabled={user.role !== "admin"}
              className="w-36 bg-slate-50 border-border font-mono" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Deposit Status</label>
            <Select value={depStatus} onValueChange={setDepStatus} disabled={user.role !== "admin"}>
              <SelectTrigger className="w-36 bg-slate-50 border-border" data-testid="deposit-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {user.role === "admin" && (
            <Button onClick={saveDeposit} disabled={busy} data-testid="deposit-save-btn" size="sm"
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold h-9">
              {busy ? "Saving…" : "Save Deposit"}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <SummaryCard label="Total Billed" value={inr(summary.total_billed)} testid="summary-billed" />
        <SummaryCard label="Total Paid" value={inr(summary.total_paid)} testid="summary-paid" accent="text-emerald-400" />
        <SummaryCard label="Outstanding" value={inr(summary.outstanding)} testid="summary-outstanding"
          accent={summary.outstanding > 0 ? "text-red-400" : ""} />
        <SummaryCard label="Total KM" value={`${summary.total_km} km`} testid="summary-km" />
        <SummaryCard label="Swaps" value={summary.swap_count} testid="summary-swaps" />
      </div>

      <div className="card-tactical overflow-x-auto" data-testid="ledger-invoices">
        <div className="p-3 border-b border-border text-xs font-medium uppercase tracking-wider text-slate-400">Invoices</div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-border">
            <th className="p-3">Invoice</th><th className="p-3">Billing Week</th><th className="p-3">KM</th>
            <th className="p-3">Amount</th><th className="p-3">Status</th><th className="p-3">Paid On / Mode</th>
          </tr></thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.invoice_id} className="border-b border-border/50" data-testid={`ledger-invoice-${inv.invoice_id}`}>
                <td className="p-3 font-mono">{inv.invoice_id}</td>
                <td className="p-3 text-slate-400 text-xs">{fmtDate(inv.week_start)} → {fmtDate(inv.week_end)}</td>
                <td className="p-3 font-mono">{inv.total_km}</td>
                <td className="p-3 font-mono font-semibold">{inr(inv.amount)}</td>
                <td className="p-3"><StatusChip label={inv.status} tone={invoiceStatusTone(inv.status)} testid={`ledger-inv-status-${inv.invoice_id}`} /></td>
                <td className="p-3 text-slate-400 text-xs">
                  {fmtDate(inv.date_paid)}
                  {inv.payment_mode && <div className="text-slate-500">{inv.payment_mode}{inv.payment_reference ? ` · ${inv.payment_reference}` : ""}</div>}
                </td>
              </tr>
            ))}
            {invoices.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card-tactical overflow-x-auto" data-testid="ledger-swaps">
        <div className="p-3 border-b border-border text-xs font-medium uppercase tracking-wider text-slate-400">Swap History</div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-border">
            <th className="p-3">Swap</th><th className="p-3">Date</th><th className="p-3">Returned</th>
            <th className="p-3">Issued</th><th className="p-3">Odometer</th>
          </tr></thead>
          <tbody>
            {swaps.map((s) => (
              <tr key={s.swap_id} className="border-b border-border/50" data-testid={`ledger-swap-${s.swap_id}`}>
                <td className="p-3 font-mono">{s.swap_id}</td>
                <td className="p-3 text-slate-400">{fmtDateTime(s.timestamp)}</td>
                <td className="p-3 font-mono text-xs">{s.old_battery_serial}</td>
                <td className="p-3 font-mono text-xs">{s.new_battery_serial}</td>
                <td className="p-3 font-mono">{s.odometer_km} km</td>
              </tr>
            ))}
            {swaps.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No swaps yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
